/**
 * The desktop WebSocket client — `src-tauri/src/ws_client.rs` behind the same small
 * interface as `websocketClient.js` (the browser one), so `liveConnections` does not care
 * which it holds.
 *
 * What it can do that a browser `WebSocket` cannot: custom handshake headers, the
 * handshake's real request + response (handed to `onOpen`), skipping certificate
 * verification, a handshake timeout, a message size limit.
 *
 * Listeners are attached BEFORE `ws_connect`: the backend emits `ws-open` from its own
 * thread and an event that beats `listen()` is lost.
 */
import { invoke } from '@tauri-apps/api/core';

let counter = 0;

export class NativeWebSocketClient {
  /**
   * @param {string} url
   * @param {(entry: {data: string, timestamp: number, direction: 'in', binary?: boolean}) => void} onMessage
   * @param {(handshake: {status: number, requestHeaders: [string, string][], responseHeaders: [string, string][]}) => void} onOpen
   * @param {(close: {code: number, reason: string}) => void} onClose
   * @param {(error: {message: string}) => void} onError
   * @param {{ headers?: Record<string, string>, options?: object }} [config]
   */
  constructor(url, onMessage, onOpen, onClose, onError, config = {}) {
    this._id = `ws-${Date.now()}-${++counter}`;
    this._open = false;
    this._closed = false;
    this._offs = [];
    this._detach = () => { while (this._offs.length) this._offs.pop()(); };
    this._start(url, onMessage, onOpen, onClose, onError, config);
  }

  async _start(url, onMessage, onOpen, onClose, onError, { headers = {}, options = {} }) {
    const id = this._id;
    const detach = this._detach;
    try {
      const { listenSafe } = await import('./safeTauriEvent');
      this._offs.push(await listenSafe(`ws-open-${id}`, (evt) => { this._open = true; onOpen?.(evt.payload); }));
      this._offs.push(await listenSafe(`ws-message-${id}`, (evt) => onMessage?.({
        data: evt.payload?.binary ? `[binary, base64] ${evt.payload.data}` : String(evt.payload?.data ?? ''),
        binary: !!evt.payload?.binary, timestamp: Date.now(), direction: 'in',
      })));
      this._offs.push(await listenSafe(`ws-close-${id}`, (evt) => { this._open = false; detach(); onClose?.(evt.payload || {}); }));
      this._offs.push(await listenSafe(`ws-error-${id}`, (evt) => { this._open = false; detach(); onError?.({ message: String(evt.payload || '') }); }));
      if (this._closed) { detach(); return; } // closed while the listeners were being attached
      await invoke('ws_connect', { id, url, headers, options });
    } catch (error) {
      detach();
      onError?.({ message: String(error?.message || error) });
    }
  }

  /** @returns {boolean} whether it was handed to the socket */
  send(message) {
    if (!this._open) return false;
    invoke('ws_send', { id: this._id, text: message }).catch(() => {});
    return true;
  }

  close() {
    if (this._closed) return;
    this._closed = true;
    this._open = false;
    // nobody listens to a closed client any more (and a server that never answers the close
    // would never send the event that used to take these off)
    this._detach();
    invoke('ws_close', { id: this._id }).catch(() => {});
  }
}
